import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Download, ExternalLink } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Label } from '@/components/elements/label'
import { Separator } from '@/components/elements/separator'
import { Skeleton } from '@/components/elements/skeleton'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { Layout } from '@/components/Layout'
import { MultiSelect } from '@/components/MultiSelect'
import { Select } from '@/components/Select'
import { useCoursesScope } from '@/pages/CoursesHub/useCoursesScope'
import {
  deleteCertificate as deleteCertificateRequest,
  getCertificate,
  updateCertificate,
} from '@/services/certificates.service'
import { getCourses } from '@/services/courses.service'
import { getEntityCourses, updateEntityCourse } from '@/services/entity_courses.service'
import { getGlobalProficiencyQuestions } from '@/services/global_settings.service'
import { getTrainings } from '@/services/trainings.service'
import { SHOW_ALTERNATE_COURSES } from '@/utils/constants'
import { cn, toast } from '@/utils/helpers'

const NO_TRAINING = { id: 'none', label: 'No Required Training' }

const CERTIFICATE_TYPES = [
  { id: 'RescueX Instructor Course', label: 'RescueX Instructor Course' },
  { id: 'Rescue Ready Instructor Course', label: 'Rescue Ready Instructor Course' },
  { id: 'Surface Rescue Instructor Course', label: 'Surface Rescue Instructor Course' },
  { id: 'Rescue Ready Student Course', label: 'Rescue Ready Student Course' },
  { id: 'Surface Rescue Student Course', label: 'Surface Rescue Student Course' },
  { id: 'Standard Student Course', label: 'Standard Student Course' },
  // Custom Student Course is intentionally NOT offered here — it's the
  // facility-only type, locked on by the entity-scoped form below.
]

// Facility-owned (custom) courses are locked to this certificate type.
const CUSTOM_STUDENT_COURSE = 'Custom Student Course'

/**
 * Checks if a certificate type requires a certificate prefix
 * @param {string} certificateType - The certificate type to check
 * @returns {boolean} - True if prefix is required
 */
const requiresCertificatePrefix = (certificateType) =>
  certificateType === 'Standard Student Course' || certificateType === CUSTOM_STUDENT_COURSE

// Module sort order in the Course Module dropdown — Published modules surface
// first, Drafts next (still selectable; Save flow prompts to publish), then
// Archived at the bottom for legacy references.
const MODULE_STATE_RANK = { Published: 0, Draft: 1, Archived: 2 }

const Certificate = () => {
  const navigate = useNavigate()
  const { certificateId } = useParams()
  const { isCustom, scopeId, scopeArgs, hubBase, rootCrumb, certLabel } = useCoursesScope()
  // Facility + org both author "custom" certs — the term mirrors the old
  // facility-only flag throughout this form.
  const isFacilityScope = isCustom

  // Form
  const form = useForm()
  const selectedTraining = form.watch('training')
  const selectedCertificateType = form.watch('certificateType')
  const certificatePrefix = form.watch('certificatePrefix')
  const {
    fields: courseRequirements,
    append: appendRequirement,
    remove: removeRequirement,
  } = useFieldArray({
    control: form.control,
    name: 'courseRequirements',
  })
  const {
    fields: courseDescriptions,
    append: appendDescription,
    remove: removeDescription,
  } = useFieldArray({
    control: form.control,
    name: 'courseDescriptions',
  })
  const {
    fields: instructorAssets,
    append: appendAsset,
    remove: removeAsset,
  } = useFieldArray({
    control: form.control,
    name: 'instructorAssets',
  })
  const { fields: proficiencyQuestions, replace } = useFieldArray({
    control: form.control,
    name: 'proficiencyQuestions',
  })

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: globalProficiencyQuestions, isPending: pendingProficiencyQuestions } = useQuery({
    queryKey: ['globalProficiencyQuestions'],
    queryFn: () => getGlobalProficiencyQuestions(),
    placeholderData: keepPreviousData,
  })
  // On the facility side the course picker offers all of the facility's own
  // (non-xverse) modules regardless of state — Drafts are allowed since the
  // Save flow prompts to publish them. Existing certs may also reference
  // Archived modules, so we don't filter on state at all here. Admin scope
  // keeps the global course list.
  const { data: courses, isPending: pendingCourses } = useQuery({
    queryKey: ['courses', scopeId],
    queryFn: () =>
      isFacilityScope
        ? getEntityCourses({
            ...scopeArgs,
            pagination: { pageSize: 1000, pageIndex: 0 },
            filters: { is_xverse_course: false },
          })
        : getCourses(null, null, null, null, true),
    placeholderData: keepPreviousData,
  })
  const { data: trainings, isPending: pendingTrainings } = useQuery({
    queryKey: ['trainings'],
    queryFn: () => getTrainings({ fullList: true }),
    placeholderData: keepPreviousData,
  })
  const { data: certificate, isPending: loadingCertificate } = useQuery({
    queryKey: ['certificates', scopeId, certificateId],
    queryFn: () => getCertificate({ ...scopeArgs, id: certificateId }),
    placeholderData: keepPreviousData,
  })
  const { isPending: loadingUpdate, mutate: mutateCertificate } = useMutation({
    mutationFn: (data) => updateCertificate({ ...scopeArgs, ...data }),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} updated successfully`)

      // Refresh certificates list
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })
  const { isPending: loadingDelete, mutate: deleteCertificate } = useMutation({
    mutationFn: () => deleteCertificateRequest({ ...scopeArgs, id: certificateId }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Course deleted successfully')

      // Refresh certificate list and redirect to the matching list page
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      navigate(`${hubBase}/courses`)
    },
  })

  const TRAINING_OPTIONS = useMemo(() => {
    const DEFAULT_OPTIONS = [NO_TRAINING]
    if (!trainings) return DEFAULT_OPTIONS

    return [
      ...DEFAULT_OPTIONS,
      ...trainings.results.map((t) => ({
        ...t,
        label: t.name,
      })),
    ]
  }, [trainings])

  // Sort Published first, then Draft, then Archived. Annotate non-Published
  // labels so the Select makes module state legible — the Save flow needs
  // the raw state too, so keep it on the option.
  const COURSES = useMemo(() => {
    if (!courses) return []

    const labelFor = (course) =>
      course.state === 'Published' ? course.name : `${course.name} (${course.state})`

    return courses.results
      .slice()
      .sort((a, b) => (MODULE_STATE_RANK[a.state] ?? 99) - (MODULE_STATE_RANK[b.state] ?? 99))
      .map((course) => ({
        label: labelFor(course),
        id: course.id,
        state: course.state,
      }))
  }, [courses])

  useEffect(() => {
    if (certificate && TRAINING_OPTIONS) {
      form.reset({
        ...certificate,
        courseRequirements: certificate.courseRequirements.map((req) => ({
          ...req,
          primaryCourse: req.primaryCourse.id || null,
          alternativeCourses: req.alternativeCourses.map((altReq) => altReq.id) || [],
        })),
      })

      // FIXME: This is a workaround to ensure the training and certificateType fields are set correctly
      setTimeout(() => {
        form.setValue('training', certificate.training || 'none')
        // Facility certs are locked to Standard Student — the type field is
        // hidden in that scope, so seed the value directly.
        form.setValue(
          'certificateType',
          isFacilityScope
            ? CUSTOM_STUDENT_COURSE
            : certificate.certificateType || 'Standard Student Course',
        )
      }, 1)
    }
  }, [certificate, TRAINING_OPTIONS, form, isFacilityScope])

  // When saving a facility cert that references Draft modules, we pause the
  // save and ask the user whether to publish those modules first. Holds the
  // pending form payload + the Draft module records to publish.
  const [draftPublishPrompt, setDraftPublishPrompt] = useState(null)

  const saveCert = (data) =>
    mutateCertificate({
      ...data,
      training: data.training === 'none' ? null : data.training,
      // Facility certs are locked to free + Custom Student. The form doesn't
      // render the cost/type fields in that scope, so force the backend
      // invariant here defensively.
      ...(isFacilityScope ? { cost: 0, certificateType: CUSTOM_STUDENT_COURSE } : {}),
    })

  const { isPending: pendingDraftPublish, mutate: publishDraftsAndSave } = useMutation({
    mutationFn: async ({ data, draftModules }) => {
      await Promise.all(
        draftModules.map((m) =>
          updateEntityCourse({ ...scopeArgs, id: m.id, state: 'Published' }),
        ),
      )
      return data
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['courses', scopeId] })
      setDraftPublishPrompt(null)
      saveCert(data)
    },
  })

  // hubBase / rootCrumb / certLabel resolve per scope (facility / org / admin).
  const breadcrumbRoot = rootCrumb
  const coursesTabHref = `${hubBase}/courses`
  const coursesTabLabel = certLabel
  const courseModuleHref = (courseModuleId) => `${hubBase}/course-modules/${courseModuleId}`

  if (loadingCertificate || pendingProficiencyQuestions || pendingCourses || pendingTrainings) {
    return (
      <Layout breadcrumbs={[breadcrumbRoot, coursesTabLabel, 'Course']}>
        <div className="p- flex h-full w-full flex-col gap-4">
          <Skeleton className="h-6 w-80 rounded-sm" />

          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
          <Separator />
          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
          <Separator />
          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  /**
   * Handles form submission. On the facility side, intercept the save when
   * any attached module is still Draft and prompt the user before continuing.
   */
  const onSubmit = (data) => {
    if (isFacilityScope && courses?.results) {
      const draftById = new Map(
        courses.results.filter((c) => c.state === 'Draft').map((c) => [c.id, c]),
      )
      const referenced = new Set()
      ;(data.courseRequirements || []).forEach((req) => {
        if (req.primaryCourse && draftById.has(req.primaryCourse)) {
          referenced.add(req.primaryCourse)
        }
        ;(req.alternativeCourses || []).forEach((id) => {
          if (draftById.has(id)) referenced.add(id)
        })
      })
      if (referenced.size > 0) {
        setDraftPublishPrompt({
          data,
          draftModules: [...referenced].map((id) => draftById.get(id)),
        })
        return
      }
    }
    saveCert(data)
  }

  return (
    <Layout
      breadcrumbs={[
        breadcrumbRoot,
        { pathname: coursesTabHref, title: coursesTabLabel },
        certificate?.name,
      ]}
    >
      <Layout.Heading title={certificate?.name} />

      <div className="mt-1 flex shrink-0 flex-row items-start justify-start gap-4 sm:justify-end">
        <ConfirmButton
          type="button"
          variant="destructive"
          className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
          disabled={loadingDelete}
          onClick={() => deleteCertificate()}
          confirmContent={`Are you sure you want to delete the course: ${certificate.name}?`}
          confirmTitle="Delete Course"
        >
          Delete Course
        </ConfirmButton>

        <Button
          type="submit"
          className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
          disabled={loadingUpdate}
          onClick={form.handleSubmit(onSubmit)}
        >
          {loadingUpdate ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="h-full w-full flex-col overflow-y-auto px-4 pb-12 sm:px-6 lg:px-8">
        <Form {...form}>
          <form className="flex flex-col gap-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <span className="text-muted-foreground font-semibold sm:w-1/4">General</span>

              <div className="flex w-full flex-col gap-8">
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input id="name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: 'This field is required' }}
                />
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea id="description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!isFacilityScope && (
                  <FormField
                    control={form.control}
                    disabled={loadingUpdate}
                    name="cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cost (per certificate)</FormLabel>
                        <FormDescription>
                          The cost in dollars that will be deducted from an entity&apos;s credit
                          balance when a student is assigned to this course.
                        </FormDescription>
                        <FormControl>
                          <div className="relative">
                            <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
                              $
                            </span>
                            <Input
                              id="cost"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              className="pl-7"
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {!isFacilityScope && (
                  <FormField
                    control={form.control}
                    disabled={loadingUpdate}
                    name="certificateType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Certificate Type</FormLabel>
                        <FormDescription>
                          Determines how the certificate is generated. Instructor courses assign
                          instructor numbers to users. Student courses generate certificate IDs.
                        </FormDescription>
                        <FormControl>
                          <Select
                            id="certificateType"
                            placeholder="Select Certificate Type"
                            options={CERTIFICATE_TYPES}
                            className="w-full"
                            {...field}
                          />
                        </FormControl>
                        {selectedCertificateType && (
                          <div className="bg-muted mt-2 rounded-md p-3 text-sm">
                            <span className="font-medium">Generated Format: </span>
                            <code className="text-primary">
                              {selectedCertificateType === 'RescueX Instructor Course' &&
                                '26-0001'}
                              {selectedCertificateType === 'Rescue Ready Instructor Course' &&
                                '26-RRINT-0001'}
                              {selectedCertificateType === 'Surface Rescue Instructor Course' &&
                                '26-SRINT-0001'}
                              {selectedCertificateType === 'Rescue Ready Student Course' &&
                                '26-RR-F1-0001'}
                              {selectedCertificateType === 'Surface Rescue Student Course' &&
                                '26-SR-F1-0001'}
                              {selectedCertificateType === 'Standard Student Course' &&
                                '26-[PREFIX]-F1-0001'}
                            </code>
                            <span className="text-muted-foreground ml-1">
                              {(selectedCertificateType === 'RescueX Instructor Course' ||
                                selectedCertificateType === 'Rescue Ready Instructor Course' ||
                                selectedCertificateType === 'Surface Rescue Instructor Course') &&
                                '(instructor number on user account)'}
                              {(selectedCertificateType === 'Rescue Ready Student Course' ||
                                selectedCertificateType === 'Surface Rescue Student Course' ||
                                selectedCertificateType === 'Standard Student Course') &&
                                '(certificate ID)'}
                            </span>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                    rules={{ required: 'Certificate type is required' }}
                  />
                )}
                {selectedCertificateType && requiresCertificatePrefix(selectedCertificateType) && (
                  <FormField
                    control={form.control}
                    disabled={loadingUpdate}
                    name="certificatePrefix"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Certificate Prefix</FormLabel>
                        <FormDescription>
                          The prefix used in certificate IDs (e.g., &quot;CPR&quot; becomes{' '}
                          {selectedCertificateType === CUSTOM_STUDENT_COURSE
                            ? '26-CPR-0001'
                            : '26-CPR-F1-0001'}
                          ). Must be unique within your account.
                        </FormDescription>
                        <FormControl>
                          <Input
                            id="certificatePrefix"
                            placeholder="e.g., CPR"
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        {certificatePrefix && (
                          <div className="bg-muted mt-2 rounded-md p-3 text-sm">
                            <span className="font-medium">Preview: </span>
                            <code className="text-primary">
                              26-{certificatePrefix.toUpperCase()}
                              {selectedCertificateType === CUSTOM_STUDENT_COURSE ? '' : '-F1'}-0001
                            </code>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                    rules={{
                      required: requiresCertificatePrefix(selectedCertificateType)
                        ? 'Certificate prefix is required for this certificate type'
                        : false,
                    }}
                  />
                )}
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="expiresAfterMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expires After (Months)</FormLabel>
                      <FormDescription>
                        Number of months until the certificate expires.
                      </FormDescription>
                      <FormControl>
                        <Input
                          id="expiresAfterMonths"
                          type="number"
                          min="1"
                          placeholder="24"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => {
                            const { value } = e.target
                            // Handle empty input explicitly
                            if (value === '' || value === null || value === undefined) {
                              field.onChange(null)
                              return
                            }
                            // Parse and validate the number
                            const parsed = parseInt(value, 10)
                            field.onChange(Number.isNaN(parsed) ? null : parsed)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <div className="sm:w-1/4">
                <span className="text-muted-foreground font-semibold">Training Settings</span>
                <p className="text-muted-foreground text-xs">
                  Configure hands-on training requirements for this course.
                </p>
              </div>

              <div className="flex w-full flex-col gap-8">
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="training"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hands-On Training</FormLabel>
                      <FormControl>
                        <Select
                          id="training"
                          placeholder="Select a Training"
                          options={TRAINING_OPTIONS}
                          className="w-full"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {selectedTraining &&
                  selectedTraining !== 'none' &&
                  proficiencyQuestions &&
                  proficiencyQuestions.length > 0 && (
                    <div className="flex w-full flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <FormLabel>Proficiency Questions</FormLabel>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            replace(
                              _.map(form.getValues('proficiencyQuestions'), (q, i) => ({
                                questionText:
                                  globalProficiencyQuestions.results[i]?.questionText || '',
                                id: q.id,
                              })),
                            )
                          }}
                        >
                          Reset to Defaults
                        </Button>
                      </div>

                      {proficiencyQuestions.map((field, index) => (
                        <div key={field.id} className="flex flex-col gap-4">
                          <FormField
                            control={form.control}
                            name={`proficiencyQuestions.${index}.questionText`}
                            render={({ field: questionField }) => (
                              <FormItem className="w-full">
                                <FormLabel>Question {index + 1}</FormLabel>
                                <FormControl>
                                  <Input {...questionField} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>

            {/* Certificate Configuration only feeds the generated certificate
                PDF, which custom (facility) courses don't produce — hide it there. */}
            {!isFacilityScope && (
              <>
                <Separator />

                <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                  <div className="sm:w-1/4">
                    <span className="text-muted-foreground font-semibold">
                      Certificate Configuration
                    </span>
                    <p className="text-muted-foreground text-xs">
                      Configure the course descriptions that appear in the certificate PDF.
                    </p>
                  </div>

                  <div className="flex w-full flex-col gap-4">
                    {courseDescriptions.map((field, index) => (
                      <div key={field.id} className="flex flex-col gap-4">
                        <div className="flex w-full flex-col gap-4">
                          <FormField
                            control={form.control}
                            name={`courseDescriptions.${index}.heading`}
                            render={({ field: descriptionField }) => (
                              <FormItem className="w-full">
                                <FormLabel>Heading</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="e.g., Adult CPR and AED use"
                                    {...descriptionField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                            rules={{ required: true }}
                          />
                          <FormField
                            control={form.control}
                            name={`courseDescriptions.${index}.content`}
                            render={({ field: descriptionField }) => (
                              <FormItem className="w-full">
                                <FormLabel>Content (optional)</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="Additional details about this course"
                                    {...descriptionField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            variant="destructive"
                            onClick={() => removeDescription(index)}
                            className="self-start"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}

                    <Button
                      className="self-start"
                      type="button"
                      variant="outline"
                      onClick={() =>
                        appendDescription({
                          heading: '',
                          content: '',
                          order: courseDescriptions.length,
                        })
                      }
                    >
                      Add Certificate Description
                    </Button>
                  </div>
                </div>
              </>
            )}

            <Separator />

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <div className="sm:w-1/4">
                <span className="text-muted-foreground font-semibold">Instructor Assets</span>
                <p className="text-muted-foreground text-xs">
                  Add files that instructors can download from the Instructor Courses section.
                </p>
              </div>

              <div className="flex w-full flex-col gap-4">
                {instructorAssets.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-4">
                    <div className="flex w-full flex-col gap-4">
                      <FormField
                        control={form.control}
                        name={`instructorAssets.${index}.fileDisplayName`}
                        render={({ field: assetField }) => (
                          <FormItem className="w-full">
                            <FormLabel>File Display Name</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Course Handbook" {...assetField} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                        rules={{ required: 'File display name is required' }}
                      />
                      <FormField
                        control={form.control}
                        name={`instructorAssets.${index}.filePath`}
                        render={({ field: assetField }) => (
                          <FormItem className="w-full">
                            <FormLabel>File</FormLabel>
                            {!assetField.value ? (
                              <FormControl>
                                <FileUploader
                                  type="aws"
                                  filePrefix="instructor-assets"
                                  maxFiles={1}
                                  handleUploadToServer={async (file) => {
                                    assetField.onChange(file.fileUploadName)
                                    if (
                                      !form.getValues(`instructorAssets.${index}.fileDisplayName`)
                                    ) {
                                      form.setValue(
                                        `instructorAssets.${index}.fileDisplayName`,
                                        file.fileName,
                                      )
                                    }
                                  }}
                                />
                              </FormControl>
                            ) : (
                              <div className="flex flex-col gap-2">
                                {certificate?.instructorAssets?.[index]?.signedFilePath && (
                                  <a
                                    href={certificate.instructorAssets[index].signedFilePath}
                                    download
                                    rel="noreferrer"
                                    className="text-foreground hover:text-primary flex items-center gap-2 text-sm font-medium"
                                  >
                                    <Download className="h-4 w-4" />
                                    <span>
                                      {certificate.instructorAssets[index].fileDisplayName}
                                    </span>
                                  </a>
                                )}
                                {!certificate?.instructorAssets?.[index]?.signedFilePath && (
                                  <p className="text-foreground text-sm font-medium">
                                    {assetField.value}
                                  </p>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    assetField.onChange('')
                                    form.setValue(`instructorAssets.${index}.fileDisplayName`, '')
                                  }}
                                  className="w-fit"
                                >
                                  Remove and Replace File
                                </Button>
                              </div>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                        rules={{ required: 'File is required' }}
                      />

                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => removeAsset(index)}
                        className="self-start"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}

                <Button
                  className="self-start"
                  type="button"
                  variant="outline"
                  onClick={() =>
                    appendAsset({
                      fileDisplayName: '',
                      filePath: '',
                      order: instructorAssets.length,
                    })
                  }
                >
                  Add Instructor Asset
                </Button>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <div className="sm:w-1/4">
                <span className="text-muted-foreground font-semibold">Course Requirements</span>
                <p className="text-muted-foreground text-xs">
                  Add course modules as requirements
                  {SHOW_ALTERNATE_COURSES && ', with alternatives if needed'}.
                </p>
              </div>

              <div className="flex w-full flex-col gap-4">
                {!SHOW_ALTERNATE_COURSES && courseRequirements.length > 0 && (
                  <Label>Course Modules</Label>
                )}
                {courseRequirements.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-4">
                    <div className="flex w-full flex-col items-center gap-4 sm:flex-row">
                      <FormField
                        control={form.control}
                        name={`courseRequirements.${index}.primaryCourse`}
                        render={({ field: requirementField }) => (
                          <FormItem className="w-full sm:w-[300px]">
                            {SHOW_ALTERNATE_COURSES && (
                              <FormLabel>Primary Course Module</FormLabel>
                            )}
                            <FormControl>
                              <Select
                                options={COURSES}
                                placeholder={`Select ${SHOW_ALTERNATE_COURSES ? 'Primary ' : ''}Course Module`}
                                {...requirementField}
                              />
                            </FormControl>
                            {requirementField.value && (
                              <a
                                href={courseModuleHref(requirementField.value)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary inline-flex w-fit items-center gap-1 text-xs hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                View module in new tab
                              </a>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                        rules={{ required: true }}
                      />
                      {SHOW_ALTERNATE_COURSES && (
                        <>
                          <span className="text-muted-foreground text-sm capitalize">OR</span>
                          <FormField
                            control={form.control}
                            name={`courseRequirements.${index}.alternativeCourses`}
                            render={({ field: requirementField }) => (
                              <FormItem className="w-full sm:w-[200px]">
                                <FormLabel>Alternative Courses</FormLabel>
                                <FormControl>
                                  <MultiSelect
                                    options={COURSES}
                                    placeholder="Select Alternative Courses"
                                    width="w-full sm:w-[200px]"
                                    {...requirementField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      )}

                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => removeRequirement(index)}
                        className="self-start"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}

                <Button
                  className="self-start"
                  type="button"
                  variant="outline"
                  onClick={() =>
                    appendRequirement({
                      primaryCourse: '',
                      alternativeCourses: [],
                      order: courseRequirements.length,
                    })
                  }
                >
                  Add Course Module
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>

      <Dialog
        open={!!draftPublishPrompt}
        onOpenChange={(open) => {
          if (!open && !pendingDraftPublish) setDraftPublishPrompt(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish attached modules?</DialogTitle>
            <DialogDescription>
              {draftPublishPrompt?.draftModules.length} module(s) attached to this course are still
              in Draft and won&apos;t be available to students until published.
            </DialogDescription>
          </DialogHeader>
          <ul className="my-2 list-disc pl-6 text-sm">
            {draftPublishPrompt?.draftModules.map((m) => (
              <li key={m.id}>{m.name}</li>
            ))}
          </ul>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={pendingDraftPublish}
              onClick={() => setDraftPublishPrompt(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={pendingDraftPublish}
              onClick={() => {
                const { data } = draftPublishPrompt
                setDraftPublishPrompt(null)
                saveCert(data)
              }}
            >
              Save without publishing
            </Button>
            <Button
              type="button"
              disabled={pendingDraftPublish}
              onClick={() => publishDraftsAndSave(draftPublishPrompt)}
            >
              {pendingDraftPublish ? 'Publishing...' : 'Publish & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}

export default Certificate
